import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import authReducer from './authSlice';
import dashboardReducer from './dashboardSlice';
import eventReducer from './eventSlice';
import eventInvitationReducer from './eventInvitationSlice';
import birthdayReducer from './birthdaySlice';
import expenseReducer from './expenseSlice';
import wishlistReducer from './wishlistSlice';
import secretSantaReducer from './secretSantaSlice';
import userReducer from './userSlice';
import userManagementReducer from './userManagementSlice';
import notificationReducer from './notificationSlice';
import trafficIndexReducer from './trafficIndexSlice';
import pollReducer from './pollSlice';
import mediaReducer from './mediaSlice';
import drinkingGameReducer from './drinkingGameSlice';
import xpReducer from './xpSlice';
import leaderboardReducer from './leaderboardSlice';
import spotifyReducer from './spotifySlice';
import featureRequestReducer from './featureRequestSlice';
import birthdayGiftReducer from './birthdayGiftSlice';
import videoWatchlistReducer from './videoWatchlistSlice';
import mediaRecommendationReducer from './mediaRecommendationSlice';

// Combine all reducers
const rootReducer = combineReducers({
  auth: authReducer,
  dashboard: dashboardReducer,
  events: eventReducer,
  eventInvitations: eventInvitationReducer,
  birthdays: birthdayReducer,
  expenses: expenseReducer,
  wishlist: wishlistReducer,
  secretSanta: secretSantaReducer,
  users: userReducer,
  userManagement: userManagementReducer,
  notifications: notificationReducer,
  trafficIndex: trafficIndexReducer,
  polls: pollReducer,
  media: mediaReducer,
  drinkingGames: drinkingGameReducer,
  xp: xpReducer,
  leaderboard: leaderboardReducer,
  spotify: spotifyReducer,
  featureRequests: featureRequestReducer,
  birthdayGifts: birthdayGiftReducer,
  videoWatchlist: videoWatchlistReducer,
  mediaRecommendation: mediaRecommendationReducer,
});

// Configure the store
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types for serialization check
        ignoredActions: ['auth/setCredentials', 'auth/logout'],
        // Ignore these paths in the state
        ignoredPaths: ['auth.user.createdAt', 'auth.user.updatedAt'],
      },
    }),
  devTools: import.meta.env.DEV,
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Typed hooks for use throughout the app
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Export store as default
export default store;
